import { AlertCircle, ArrowRight, CheckCircle2, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { GuidedStepFeedbackBlock } from "@/types/agentic-chat";
import type { GuidedLessonControlInput } from "@/types/student-toofi";
import { detectGuidedStepLocale, getGuidedUiCopy } from "@/components/ui/reader-ai/guided-step-copy";

type GuidedStepFeedbackCardProps = {
  block: GuidedStepFeedbackBlock;
  onContinue?: (input: GuidedLessonControlInput) => void;
};

export function GuidedStepFeedbackCard({ block, onContinue }: GuidedStepFeedbackCardProps) {
  const locale = detectGuidedStepLocale(`${block.feedback}\n${block.hint ?? ""}\n${block.revealAnswer ?? ""}`);
  const isRtl = locale === "ar";
  const copy = getGuidedUiCopy(locale);
  const isRevealState = !block.correct && Boolean(block.revealAnswer) && block.proceedToNext;
  const toneClass = block.correct
    ? "border-emerald-200/80 bg-emerald-50/75 text-emerald-800"
    : isRevealState
      ? "border-blue-200/80 bg-blue-50/80 text-blue-900"
      : "border-amber-200/80 bg-amber-50/80 text-amber-900";
  const stateLabel = block.correct
    ? copy.correctLabel
    : isRevealState
      ? copy.revealStateLabel
      : copy.hintStateLabel;

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="rounded-[1.25rem] border border-slate-200/80 bg-white/96 p-3 shadow-[0_16px_32px_rgba(148,163,184,0.10)]"
    >
      <div className="flex items-start justify-between gap-2">
        <Badge variant="outline" className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${toneClass}`}>
          {stateLabel}
        </Badge>
        <span className="text-[11px] font-medium text-slate-500">
          {copy.step} {block.stepIndex}
        </span>
      </div>
      <div className="mt-2 flex gap-2">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            block.correct
              ? "bg-emerald-100 text-emerald-700"
              : isRevealState
                ? "bg-blue-100 text-blue-700"
                : "bg-amber-100 text-amber-700"
          }`}
        >
          {block.correct ? <CheckCircle2 className="h-4.5 w-4.5" /> : <AlertCircle className="h-4.5 w-4.5" />}
        </div>
        <div className="min-w-0 space-y-2">
          <p className="text-[13px] leading-6 text-slate-800">{block.feedback}</p>
          {block.hint ? (
            <div className="rounded-[0.95rem] border border-amber-100 bg-amber-50/70 px-3 py-2 text-[12px] leading-6 text-amber-900">
              <span className="font-semibold">{copy.hintLabel}: </span>
              {block.hint}
            </div>
          ) : null}
          {block.revealAnswer ? (
            <div className="rounded-[0.95rem] border border-blue-100 bg-blue-50/70 px-3 py-2 text-[12px] leading-6 text-slate-800">
              <span className="font-semibold text-slate-900">{copy.revealLabel}: </span>
              {block.revealAnswer}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          variant={block.proceedToNext ? "default" : "outline"}
          className="h-10 rounded-full px-4 text-[12px] font-semibold"
          onClick={() =>
            onContinue?.({
              lessonId: block.lessonId,
              stepIndex: block.stepIndex,
            })
          }
        >
          {block.proceedToNext ? <ArrowRight className="me-2 h-4 w-4" /> : <RotateCcw className="me-2 h-4 w-4" />}
          {block.proceedToNext ? copy.next : copy.tryAgain}
        </Button>
      </div>
    </div>
  );
}
